import React, { useEffect, useState, useRef } from 'react';
import { CertificationType, FileTypeDetails, FileTableEntry, CertificationDocumentMetadata, FileType, FileMetadataType, FileMetadata } from '../../../store/Models/Documents/DocumentModels';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { DropdownMenu } from "../DropdownMenu";

interface Props {
    fileTypes?: FileTypeDetails[];
    dataSource?: FileTableEntry[];
    certificationTypes?: { certificationType: string, friendlyName: string }[];
    uploadFileAction: (fileDetails: FileTableEntry, file: File, typeId: number, index?:number) => Promise<FileTableEntry>;
    metadataChangeAction: (file: FileTableEntry) => void;
    deleteFileAction: (fileId: string) => Promise<void>;
    addButtonLocalised: string;
    localizer: IStringLocalizer;
    inventoryId?: string;
    questionIndex?: number;
    shouldAddButtonDisabled?: boolean;
    isDisabled?: boolean;
}

const FileUploader: React.FC<Props> = ({ fileTypes, certificationTypes, dataSource, uploadFileAction, metadataChangeAction, deleteFileAction, addButtonLocalised, localizer, inventoryId, questionIndex, shouldAddButtonDisabled, isDisabled = false }) => {

    const defaultFileTypes: FileTypeDetails[] = [
        {
            displayValue: localizer.localizeString("Other File"),
            value: FileMetadataType.PlainDocument
        },
        {
            displayValue: localizer.localizeString("Certification"),
            value: FileMetadataType.Certification
        }];

    const defaultCertificationTypes = [
        {
            certificationType: CertificationType.C2c,
            friendlyName: localizer.localizeString("C2C")
        },
        {
            certificationType: CertificationType.Declare,
            friendlyName: localizer.localizeString("Declare")
        },
        {
            certificationType: CertificationType.Epd,
            friendlyName: localizer.localizeString("EPD")
        },
        {
            certificationType: CertificationType.Hpd,
            friendlyName: localizer.localizeString("HPD")
        },
        {
            certificationType: CertificationType.Voc,
            friendlyName: localizer.localizeString("VOC")
        },
        {
            certificationType: CertificationType.Other,
            friendlyName: localizer.localizeString("Other")
        },
    ];

    if (!fileTypes) {
        fileTypes = defaultFileTypes;
    }

    if (!certificationTypes) {
        certificationTypes = defaultCertificationTypes;
    }

    const [files, setFiles] = useState<FileTableEntry[]>(dataSource || []);
    const [selectedFileType, setSelectedFileType] = useState<FileTypeDetails>({ displayValue: "", value: 0 });
    const [showDropdown, setShowDropdown] = useState(false);
    const deletedFileIds = useRef(new Set());
    const isAddDisabled = isDisabled || !!shouldAddButtonDisabled;
    
    // Update files state when dataSource prop changes
    useEffect(() => {
        if (dataSource) {
            // Filter out any files that were marked as deleted
            const filteredDataSource = dataSource.filter(file =>
                !deletedFileIds.current.has(file.fileId)
            );
            setFiles(filteredDataSource);
        } else {
            setFiles([]);
        }
    }, [dataSource, shouldAddButtonDisabled, isDisabled]);

    useEffect(() => {
        if (isDisabled && showDropdown) {
            setShowDropdown(false);
        }
    }, [isDisabled, showDropdown]);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>,index?:number) => {
        if (isDisabled) {
            return;
        }

        const fileList = event.target.files;
        const updatedFiles: FileTableEntry[] = [];

        if (fileList) {
            for (let i = 0; i < fileList.length; i++) {
                var metadata = {
                    description: "",
                    expirationDate: "",
                    level: "",
                    otherTypeValue: "",
                    type:""
                } as FileMetadata;
                const file = fileList[i];
                let newFile: FileTableEntry = {
                    fileId: i.toString(),
                    filename: file.name,
                    type: FileType.GeneralFile,
                    metadataType: selectedFileType.value,
                    inventoryId: inventoryId ?? "",
                    metadata: metadata,
                    metadataJson: typeof metadata == "string" ? metadata : JSON.stringify(metadata)
                };

                newFile = await uploadFileAction(newFile, file, selectedFileType.value, index);
                updatedFiles.push(newFile);
            }
        }

        setFiles(prevFiles => {
            const existingIds = new Set(prevFiles.map(f => f.fileId));
            const newOnes = updatedFiles.filter(f => !existingIds.has(f.fileId));
            return [...prevFiles, ...newOnes];
        });


    };

    const handleMetadataPropChange = async (index: number, event: React.ChangeEvent<HTMLInputElement>, propName: string) => {
        if (isDisabled) {
            return;
        }

        const updatedFiles = [...files];
        let currentFile = updatedFiles[index];
        let fileMetadata = (typeof currentFile.metadata == "string" ? JSON.parse(currentFile.metadata) : currentFile.metadata) as any;
        if (!fileMetadata) {
            fileMetadata = {
                description: "",
                expirationDate: "",
                level: "",
                otherTypeValue: "",
                type:""
            };
        }

        fileMetadata[propName] = event.target.value;
        currentFile.metadataJson = JSON.stringify(fileMetadata);
        setFiles(updatedFiles);
        await metadataChangeAction(currentFile);
    };

    const handleDropdownPropChange = async (index: number, value: string, propName: string) => {
        if (isDisabled) {
            return;
        }

        const updatedFiles = [...files];
        let file = updatedFiles[index] as any;
        let fileMetadata = (typeof file.metadata == "string" ? JSON.parse(file.metadata) : file.metadata) as any;

        fileMetadata[propName] = value;
        file.metadataJson = JSON.stringify(fileMetadata);
        setFiles(updatedFiles);

        await metadataChangeAction(file);
    };

    const handleFileTypeChange = (type: FileTypeDetails): void => {
        if (isDisabled) {
            return;
        }

        setSelectedFileType(type);
        setShowDropdown(false);
        const fileInput = document.getElementById(`file-input-${questionIndex}`) as HTMLInputElement;

        if (fileInput) {
            fileInput.click();
        }
    };

    const handleDeleteFile = async (index: number) => {
        if (isDisabled) {
            return;
        }

        const updatedFiles = [...files];
        let deletedFile = updatedFiles[index];

        // Add to deleted files set
        deletedFileIds.current.add(deletedFile.fileId);

        await deleteFileAction(deletedFile.fileId);
        updatedFiles.splice(index, 1);
        setFiles(updatedFiles);
    };

    const handleAddDocument = () => {
        if (isAddDisabled) {
            return;
        }

        setShowDropdown(!showDropdown);
    };

    const getPredefinedValue = (file: FileTableEntry) => {
        let metadata = file.metadata as CertificationDocumentMetadata;
        return metadata?.type && metadata.type.length > 0
            ? defaultCertificationTypes.first(c => c.certificationType == metadata?.type || c.friendlyName == metadata?.type).certificationType
            : defaultCertificationTypes.first(c => c.certificationType == CertificationType.Other).certificationType;
    }

    const getFriendlyNameForCertificationType = (type: string) => {
        return defaultCertificationTypes.find(cert => cert.certificationType == type)?.friendlyName ?? "";

    }

    return (
        <div className="file-links w-100">
            <div className={`btn-group open ${files.length ? "m-b-15" : ""}`}>
                <button
                    className="btn btn-sm btn-default"
                    onClick={handleAddDocument}
                    disabled={isAddDisabled}>
                    <span className="glyphicon glyphicon-plus"></span>&nbsp;
                    {addButtonLocalised}
                </button>
                {showDropdown && (
                    <div>
                        <ul className="dropdown-menu" style={{ display: 'block' }}>
                            {fileTypes.map((fileType) => (
                                <li key={fileType.value}>
                                    <a className="documents-add-file-button"
                                        onClick={() => handleFileTypeChange(fileType)}>{fileType.displayValue}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div className="file-link-container form-inline">
                <input
                    id={`file-input-${questionIndex}`}
                    type="file"
                    multiple
                    disabled={isDisabled}
                    onChange={(e) => {handleFileChange(e,questionIndex)}}
                    onClick={(event) => {(event.target as HTMLInputElement).value = '' }}
                    style={{ display: 'none' }}
                />
                <div className="file-links w-100">
                    {files.map((file, index) => (
                        <div key={index} className="file-link-container form-inline">
                            <a href={inventoryId ? `Api/InventoryFile?inventoryId=${file.inventoryId}&fileId=${file.fileId}` : `Api/GeneralFile?fileId=${file.fileId}`}>{file.filename}</a>
                            <div className="file-metadata-form">
                                <input
                                    className="form-control file-description-input m-r-5"
                                    type="text"
                                    disabled={isDisabled}
                                    value={file.metadata?.description}
                                    onChange={(event) => handleMetadataPropChange(index, event, 'description')}
                                    placeholder={localizer.localizeString("description")}    
                                />
                                {(file.metadataType === 'Certification' || file.metadataType === FileMetadataType.Certification || file.metadataType === FileMetadataType.Certification.toString()) &&
                                    <>
                                        <input
                                            className="form-control file-description-input m-r-5"
                                            type="text"
                                            disabled={isDisabled}
                                            value={(file.metadata as CertificationDocumentMetadata)?.level}
                                            onChange={(event) => handleMetadataPropChange(index, event, 'level')}
                                        placeholder={localizer.localizeString("level")}
                                        />
                                        <input
                                            type="date"
                                            disabled={isDisabled}
                                            value={(file.metadata as CertificationDocumentMetadata)?.expirationDate}
                                            className="form-control cert-expiration-date m-r-5"
                                            onChange={(event) => handleMetadataPropChange(index, event, 'expirationDate')}
                                    />
                                    <DropdownMenu options={certificationTypes ? certificationTypes.map(o => o.certificationType) : []}
                                            id="certification"
                                            isDisabled={isDisabled}
                                            onOptionSelected={(event) => handleDropdownPropChange(index, event, 'type')}
                                            getFriendlyNameForOptions={getFriendlyNameForCertificationType}
                                            getUniqueValueForOption={o => o}
                                            preselectedOption={getPredefinedValue(file)}>
                                        </DropdownMenu>
                                    </>
                                }
                                <button onClick={() => handleDeleteFile(index)}
                                    disabled={isDisabled}
                                    className={`btn btn-toxnot-danger btn-sm field-answer-remove-file-button ${file.metadataType === 'Certification' || file.metadataType === FileMetadataType.Certification || file.metadataType === FileMetadataType.Certification.toString() ? 'm-l-5' : ''}`}>
                                    <span className="glyphicon glyphicon-remove"></span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FileUploader;
